import React, { useState, useEffect } from 'react';
import {
  ActivityIndicator,
  Modal, Pressable, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import Colors from '../../constants/Colors';
import CloseModalIcon from '../CloseModalIcon';
import TickIcon from '../TickIcon';
import CloseIconSmall from '../12pxIcons/CloseIconSmall';
import { TAG_COLORS, TagColor, UserPermission } from '../../constants/Types';
import isMobile from '../IsMobile';
import TabButton from '../TabButton';
import CustomTextField from '../CustomTextField';
import {
  DEFAULT_ROLE_READ_PERMISSIONS,
  ensureDefaultRoleReadPermissions,
} from '../../constants/UserPermissions';

const permissionRows = [
  {
    label: 'Tickets',
    permissions: [
      UserPermission.TICKETS_CREATE,
      UserPermission.TICKETS_READ,
      UserPermission.TICKETS_UPDATE,
      UserPermission.TICKETS_DELETE,
    ],
  },
  {
    label: 'Templates',
    permissions: [
      UserPermission.TEMPLATES_CREATE,
      UserPermission.TEMPLATES_READ,
      UserPermission.TEMPLATES_UPDATE,
      UserPermission.TEMPLATES_DELETE,
    ],
  },
  {
    label: 'Assets',
    permissions: [
      UserPermission.ASSETS_CREATE,
      UserPermission.ASSETS_READ,
      UserPermission.ASSETS_UPDATE,
      UserPermission.ASSETS_DELETE,
    ],
  },
  {
    label: 'User Groups',
    permissions: [
      UserPermission.USER_GROUPS_CREATE,
      UserPermission.USER_GROUPS_READ,
      UserPermission.USER_GROUPS_UPDATE,
      UserPermission.USER_GROUPS_DELETE,
    ],
  },
  {
    label: 'Users',
    permissions: [
      UserPermission.USERS_CREATE,
      UserPermission.USERS_READ,
      UserPermission.USERS_UPDATE,
      UserPermission.USERS_DELETE,
    ],
  },
  {
    label: 'Locations',
    permissions: [
      UserPermission.LOCATIONS_CREATE,
      UserPermission.LOCATIONS_READ,
      UserPermission.LOCATIONS_UPDATE,
      UserPermission.LOCATIONS_DELETE,
    ],
  },
];

export default function EditRoleModal(props) {
  const [currentPermissions, setCurrentPermissions] = useState(
    ensureDefaultRoleReadPermissions(props.permissions || props.role?.permissions || []),
  );
  const [roleName, setRoleName] = useState(props.role?.name || '');
  const [roleDescription, setRoleDescription] = useState(props.role?.description || '');
  const [saving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState('ESSENTIAL_INFO');
  const [roleColor, setRoleColor] = useState(props.role?.color || 'blue');

  const hasReadPermission = currentPermissions.some((p) => p.endsWith('READ'));

  useEffect(() => {
    setCurrentPermissions(ensureDefaultRoleReadPermissions(props.role?.permissions || []));
    setRoleName(props.role?.name || '');
    setRoleDescription(props.role?.description || '');
    setSelectedTab('ESSENTIAL_INFO');
    setRoleColor(props.role?.color || 'blue');
  }, [props.role?.permissions, props.role?.name, props.role?.description, props.role?.color]);

  const togglePermission = (permission) => {
    const newPermissions = [...currentPermissions];
    const index = newPermissions.indexOf(permission);
    const base = permission.substring(0, permission.lastIndexOf('_'));

    if (index > -1) {
      if (DEFAULT_ROLE_READ_PERMISSIONS.includes(permission)) return;

      newPermissions.splice(index, 1);

      if (permission.endsWith('READ')) {
        [`${base}_CREATE`, `${base}_UPDATE`, `${base}_DELETE`].forEach((dependentPermission) => {
          const dependentIndex = newPermissions.indexOf(dependentPermission);
          if (dependentIndex > -1) newPermissions.splice(dependentIndex, 1);
        });
      }
    } else {
      newPermissions.push(permission);

      if (
        permission.endsWith('CREATE')
        || permission.endsWith('UPDATE')
        || permission.endsWith('DELETE')
      ) {
        const readPermission = `${base}_READ`;

        if (!newPermissions.includes(readPermission)) {
          newPermissions.push(readPermission);
        }
      }
    }
    setCurrentPermissions(newPermissions);
  };

  const close = () => {
    setCurrentPermissions(ensureDefaultRoleReadPermissions(
      props.permissions || props.role?.permissions || [],
    ));
    setRoleName(props.role?.name || '');
    setRoleDescription(props.role?.description || '');
    setSelectedTab('ESSENTIAL_INFO');
    setRoleColor(props.role?.color || 'blue');
    props.onClose();
  };

  const HasPermission = ({ permission }) => (
    <View style={{ flex: 1, alignItems: 'flex-start' }}>
      <Pressable
        onPress={() => togglePermission(permission)}
        style={{
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: 20,
          paddingHorizontal: 2,
          borderWidth: 1,
          borderColor: Colors.light.hereworksBlue100,
          borderRadius: 4,
        }}
      >
        {currentPermissions.includes(permission) ? (
          <TickIcon />
        ) : (
          <CloseIconSmall />
        )}
      </Pressable>
    </View>
  );

  const ColumnHeader = ({ text, flex = 1 }) => (
    <Text
      style={{
        fontSize: 10,
        marginBottom: 10,
        flex: flex || 1,
        fontFamily: 'Roboto_500Medium',
        color: Colors.light.hereworksBlack400,
      }}
    >
      {text}
    </Text>
  );

  return (
    <Modal
      visible={props.isVisible}
      presentationStyle={'formSheet'}
      transparent={!isMobile}
      onRequestClose={() => close()}
      animationType={isMobile ? 'slide' : 'fade'}
    >
      <Pressable
        style={{
          flex: 1,
          backgroundColor: isMobile ? '#fff' : 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          padding: isMobile ? 0 : 20,
        }}
        disabled={isMobile}
        onPress={() => {
          if (!isMobile) close();
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          disabled={isMobile}
          style={{
            flex: isMobile ? 1 : undefined,
            marginHorizontal: isMobile ? 0 : 40,
            borderRadius: 10,
            backgroundColor: isMobile ? '#fff' : 'white',
            maxHeight: isMobile ? '100%' : '80%',
            cursor: 'auto',
          }}
        >
          <ScrollView
            style={{
              backgroundColor: 'white',
              borderRadius: 10,
              padding: 20,
              flex: isMobile ? 1 : undefined,
            }}
            contentContainerStyle={{ paddingBottom: 40 }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
              <Text
                style={{
                  fontSize: isMobile ? 14 : 18,
                  fontFamily: 'Poppins_600SemiBold',
                  textAlign: 'center',
                  flex: 1,
                  marginBottom: 16,
                }}
              >
                {props.role?.name ? 'Edit Role' : 'Create Role'}
              </Text>
              <Pressable
                onPress={() => close()}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  padding: 10,
                  width: isMobile ? 20 : 40,
                  height: isMobile ? 20 : 40,
                  borderRadius: isMobile ? 0 : 20,
                  backgroundColor: isMobile ? 'transparent' : Colors.light.hereworksBlue100,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CloseModalIcon />
              </Pressable>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 20 }}>
              <TabButton title="ESSENTIAL INFO"
                select={() => {
                  setSelectedTab('ESSENTIAL_INFO');
                }}
                selected={selectedTab === 'ESSENTIAL_INFO'} />
              <TabButton title="PERMISSIONS"
                select={() => {
                  setSelectedTab('PERMISSIONS');
                }}
                selected={selectedTab === 'PERMISSIONS'} />
            </View>
            {selectedTab === 'ESSENTIAL_INFO' && (
            <View style={{ marginBottom: 20 }}>
              <Text style={{
                marginBottom: 10,
                color: Colors.light.hereworksBlack400,
                fontSize: 12,
                textAlign: 'center',
                fontFamily: 'Poppins_400Regular',
              }}>
                {'Fields marked with * are mandatory'}
              </Text>
              <Text style={{
                fontSize: 14,
                color: Colors.light.hereworksBlack600,
                marginBottom: 8,
              }}>
                {'Role name *'}
              </Text>
              <CustomTextField
                placeholder="Enter role name"
                value={roleName}
                updateValue={setRoleName}
              />
              <Text style={{
                fontSize: 14,
                color: Colors.light.hereworksBlack600,
                marginVertical: 8,
              }}>
                {'Role description'}
              </Text>
              <CustomTextField
                placeholder="Enter role description"
                value={roleDescription}
                updateValue={setRoleDescription}
                multiline
                maxLength={150}
                indicateLength
              />
              <Text style={{
                fontSize: 14,
                color: Colors.light.hereworksBlack600,
                marginVertical: 8,
              }}
              >
                {'Tag colour'}
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  gap: 12,
                  paddingVertical: 10,
                }}
              >
                {Object.entries(TAG_COLORS).map(([key, color]) => {
                  const isSelected = roleColor === key;

                  return (
                    <Pressable
                      key={key}
                      onPress={() => setRoleColor(key as TagColor)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        backgroundColor: isSelected ? color.text : color.background,
                        borderWidth: 4,
                        borderColor: color.text,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }} />
                  );
                })}
              </View>
              <Text
                style={{
                  fontSize: 12,
                  color: TAG_COLORS[roleColor as TagColor].text,
                  backgroundColor: TAG_COLORS[roleColor as TagColor].background,
                  alignSelf: 'flex-start',
                  paddingHorizontal: 12,
                  paddingVertical: 4,
                  borderRadius: 12,
                  fontFamily: 'Poppins_500Medium',
                  marginBottom: 10,
                }}
              >Sample tag</Text>
            </View>)}
            {selectedTab === 'PERMISSIONS' && (
              <View style={{ marginBottom: 20 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <ColumnHeader text="PERMISSIONS" flex={2} />
                  <ColumnHeader text="CREATE" />
                  <ColumnHeader text="VIEW" />
                  <ColumnHeader text="EDIT" />
                  <ColumnHeader text="DELETE" />
                </View>
                {permissionRows.map((row) => (
                  <View style={styles.row} key={row.label}>
                    <Text style={styles.rowTitle}>{row.label}</Text>
                    {row.permissions.map((p) => (
                      <HasPermission permission={p} key={p} />
                    ))}
                  </View>
                ))}
              </View>)}
            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
              <Pressable
                onPress={() => close()}
                style={{
                  backgroundColor: '#fff',
                  padding: 12,
                  borderRadius: 8,
                  alignItems: 'center',
                  marginTop: 20,
                  marginRight: 10,
                  borderWidth: 1,
                  borderColor: Colors.light.hereworksBlue100,
                }}
              >
                <Text
                  style={{
                    color: Colors.light.hereworksBlue500,
                    fontFamily: 'Roboto_500Medium',
                  }}
                >
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                disabled={selectedTab !== 'ESSENTIAL_INFO'
                  && (roleName.trim().length === 0 || !hasReadPermission || saving)}
                onPress={() => {
                  (async () => {
                    if (selectedTab === 'ESSENTIAL_INFO') setSelectedTab('PERMISSIONS');
                    else {
                      setSaving(true);
                      try {
                        const saveSucceeded = await props.onSave(
                          roleName,
                          roleDescription,
                          currentPermissions,
                          props.role?.id,
                          roleColor,
                        );

                        if (saveSucceeded) {
                          close();
                        }
                      } finally {
                        setSaving(false);
                      }
                    }
                  })();
                }}
                style={{
                  backgroundColor: Colors.light.hereworksBlue500,
                  padding: 12,
                  borderRadius: 8,
                  alignItems: 'center',
                  marginTop: 20,
                  borderWidth: 1,
                  borderColor: Colors.light.hereworksBlue500,
                  opacity: (selectedTab !== 'ESSENTIAL_INFO'
                    && (roleName.trim().length === 0 || !hasReadPermission || saving)) ? 0.5 : 1,
                }}
              >
                <Text
                  style={{
                    color: 'white',
                    fontFamily: 'Roboto_500Medium',
                  }}
                >
                  {selectedTab === 'ESSENTIAL_INFO' ? 'Continue' : 'Save'}
                </Text>
              </Pressable>
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
      {saving && (
        <View style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', borderRadius: 8,
        }}>
          <ActivityIndicator animating={saving} />
        </View>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginVertical: 12,
    borderColor: Colors.light.hereworksBlue100,
  },
  rowTitle: {
    fontSize: 14,
    marginBottom: 10,
    flex: 2,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
});
